import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubformField } from '@shared/data/entity/fields/SubformField';
import { CardEntityError, useFormErrors } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { useEntityFormViewTemplate } from '@shared/scenes/entity/util/useEntityFormViewTemplate';
import { useViewComponents } from '@shared/scenes/entity/util/useViewComponents';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { FieldRecord, UpdateField } from '../../types';
import { useEditFields } from './useEditFields';
import { useSaveEntityFields } from './useSaveCardEntity';

type Status = 'loading' | 'error' | 'input' | 'success';

interface EditCardEntity {
  components: API.AnyComponent[];
  fields: FieldRecord | null;
  errors: CardEntityError[];
  viewTemplateTitle: string;
  viewTemplate?: API.ViewTemplateFragment;
  updateField: UpdateField;
  onPressSave: () => void;
  isLoading: boolean;
  isSaving: boolean;
  status: Status;
  isDirty: boolean;
  threadId?: string;
  /** True once save has been attempted while the form has errors. Passed to
   *  SubformNavigationContext to trigger per-subform error display. */
  saveAttempted: boolean;
  /** Component IDs that have been modified both locally and by a remote user. */
  conflictingComponentIds: string[];
  /** Reverts conflicting fields to the last-known remote values and clears conflict state. */
  revertConflicts: () => void;
}

export function useEditCardEntity({
  entityId,
  workflowTemplateId,
  viewTemplateId,
  isReadOnly = false,
}: {
  entityId: string;
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  isReadOnly?: boolean;
}): EditCardEntity {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const [status, setStatus] = useState<Status>('loading');
  const [saveAttemptedWithLocalErrors, setSaveAttemptedWithLocalErrors] = useState(false);
  const {
    fields,
    rawFields,
    modifiedFields,
    updateField,
    isDirty,
    resetDirty,
    threadId,
    loading: fieldsLoading,
    errors: fieldsErrors,
    conflictingComponentIds,
    revertConflicts,
  } = useEditFields(entityId, workflowTemplate);

  const viewTemplate = useEntityFormViewTemplate({
    workflowTemplateId,
    viewTemplateId,
    selectorFields: rawFields,
    type: 'EDIT',
  });

  const {
    saveEntity,
    loading: saveEntityLoading,
    errors: apiErrors,
  } = useSaveEntityFields(entityId, workflowTemplate, viewTemplate?.id);

  const components = useViewComponents({
    workflowTemplate,
    viewTemplate,
    includeServerManagedComponents: true,
    isReadOnly,
  });

  const { errors, isValid } = useFormErrors({
    apiErrors: [...apiErrors, ...fieldsErrors],
    fields,
    components,
    includeOnSubmitErrors: saveAttemptedWithLocalErrors,
    includeLocalErrors: saveAttemptedWithLocalErrors,
  });

  const hasSubformRequiredErrors = useMemo(
    () =>
      components.some(component => {
        const field = fields?.[component.id];
        return field instanceof SubformField && field.hasUnfilledRequiredFields();
      }),
    [components, fields],
  );

  useEffect(() => {
    if (fieldsLoading || isNil(fields) || isNil(viewTemplate)) {
      setStatus('loading');
    } else if (!isEmpty(errors)) {
      setStatus('error');
    } else {
      setStatus('input');
    }
  }, [errors, fieldsLoading, status, fields, viewTemplate]);

  useEffect(() => {
    if (isValid && !hasSubformRequiredErrors) {
      setSaveAttemptedWithLocalErrors(false);
    }
  }, [isValid, hasSubformRequiredErrors]);

  const onPressSave = useCallback(async () => {
    if (!isValid || hasSubformRequiredErrors) {
      setSaveAttemptedWithLocalErrors(true);
      return;
    }
    setSaveAttemptedWithLocalErrors(false);

    if (!saveEntityLoading && !isNil(modifiedFields)) {
      try {
        await saveEntity(modifiedFields);
        resetDirty();
        setStatus('success');
      } catch (error) {
        setStatus('error');
      }
    }
  }, [
    saveEntityLoading,
    modifiedFields,
    saveEntity,
    isValid,
    hasSubformRequiredErrors,
    resetDirty,
  ]);

  return {
    components,
    fields,
    errors,
    viewTemplateTitle: viewTemplate?.name ?? '',
    viewTemplate,
    updateField,
    onPressSave,
    isLoading: status === 'loading',
    isSaving: saveEntityLoading,
    status,
    isDirty,
    threadId,
    saveAttempted: saveAttemptedWithLocalErrors,
    conflictingComponentIds,
    revertConflicts,
  };
}
